// Auth Configs (rbac-restore R3 3.2): bring-your-own OAuth apps over
// gateway/api/auth_configs.py.
//
//   GET    /auth-configs        view_servers            own rows + platform rows (read-only)
//   POST   /auth-configs        manage_auth_configs + auth_configs.byo   credentials only
//   PATCH  /auth-configs/{id}   manage_auth_configs + auth_configs.byo
//   DELETE /auth-configs/{id}   manage_auth_configs + auth_configs.byo   409 while pinned
//
// The create body carries only the route's allowed keys (`_CREATE_FIELDS`); anything
// else is a 422 there. Platform rows (scope "platform") never get an action.
//
// The connect-link redirect-origins editor (PATCH /workspaces/{id}/connect-redirect-origins)
// lives on the Workspace page (RedirectOriginsSection, Developer mode), which reads the
// stored list from GET /workspaces/{id}.
import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch, isApiError } from '../api/client'
import { errorMessage } from '../api/errors'
import { qk } from '../api/queries'
import type {
  AuthConfigsResponse,
  WorkspaceAuthConfig,
  WorkspaceAuthConfigCreate,
  WorkspaceAuthConfigUpdate,
} from '../api/types'
import { useSession } from '../context/SessionContext'
import { useCan } from '../rbac'
import { useFeature, type Gate } from '../entitlements'
import { PlanNote } from '../entitlements/PlanNote'
import { ConfirmAction } from '../components/ConfirmAction'
import { CopyButton } from '../components/CopyButton'
import { Icon } from '../components/Icon'
import { Modal } from '../components/Modal'
import { Badge, EmptyState, PageHeader, QueryState } from '../components/ui'

function splitScopes(v: string): string[] {
  return [...new Set(v.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean))]
}

function CreateModal({ providers, onClose }: { providers: string[]; onClose: () => void }) {
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const [provider, setProvider] = useState(providers[0] ?? '')
  const [name, setName] = useState('')
  const [clientId, setClientId] = useState('')
  const [secret, setSecret] = useState('')
  const [scopes, setScopes] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const create = useMutation({
    mutationFn: (body: WorkspaceAuthConfigCreate) =>
      apiFetch<WorkspaceAuthConfig>('/auth-configs', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.authConfigs(workspaceId) })
      onClose()
    },
  })
  const ready = provider && name.trim() && clientId.trim() && secret
  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!ready || create.isPending) return
    const scopeList = splitScopes(scopes)
    // Only the route's allowed fields; scopes omitted ⇒ the platform template's.
    create.mutate({
      provider,
      name: name.trim(),
      client_id: clientId.trim(),
      client_secret: secret,
      is_default: isDefault,
      ...(scopeList.length ? { scopes: scopeList } : {}),
    })
  }
  return (
    <Modal title="New auth config" onClose={onClose}>
      <form onSubmit={onSubmit} aria-label="New auth config form">
        <div className="field">
          <label htmlFor="ac-provider">Provider</label>
          <select id="ac-provider" value={provider} onChange={(e) => setProvider(e.target.value)}>
            {providers.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="ac-name">Name</label>
          <input id="ac-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={64} placeholder="e.g. production" />
        </div>
        <div className="field">
          <label htmlFor="ac-client-id">Client ID</label>
          <input id="ac-client-id" value={clientId} onChange={(e) => setClientId(e.target.value)} maxLength={512} autoComplete="off" />
        </div>
        <div className="field">
          <label htmlFor="ac-secret">Client secret</label>
          <input id="ac-secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} maxLength={2048} autoComplete="new-password" />
          <span className="field-hint">Stored encrypted. It is never shown again.</span>
        </div>
        <div className="field">
          <label htmlFor="ac-scopes">Scopes (optional)</label>
          <input id="ac-scopes" value={scopes} onChange={(e) => setScopes(e.target.value)} placeholder="Leave empty to use the platform defaults" />
        </div>
        <label className="check">
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
          Make this the workspace default for {provider || 'the provider'}
        </label>
        {create.error ? <p className="form-error" role="alert">{errorMessage(create.error)}</p> : null}
        <div className="modal-foot modal-foot--inline">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={!ready || create.isPending} aria-busy={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function EditModal({ config, onClose }: { config: WorkspaceAuthConfig; onClose: () => void }) {
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const [name, setName] = useState(config.name ?? '')
  const [secret, setSecret] = useState('')
  const [scopes, setScopes] = useState(config.scopes.join(' '))
  const [isDefault, setIsDefault] = useState(config.is_default)
  const [enabled, setEnabled] = useState(config.enabled)
  const save = useMutation({
    mutationFn: (body: WorkspaceAuthConfigUpdate) =>
      apiFetch<WorkspaceAuthConfig>(`/auth-configs/${config.id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.authConfigs(workspaceId) })
      onClose()
    },
  })
  // Send only what changed.
  const body: WorkspaceAuthConfigUpdate = {}
  if (name.trim() && name.trim() !== (config.name ?? '')) body.name = name.trim()
  if (secret) body.client_secret = secret
  const scopeList = splitScopes(scopes)
  if (scopeList.join(' ') !== config.scopes.join(' ')) body.scopes = scopeList
  if (isDefault !== config.is_default) body.is_default = isDefault
  if (enabled !== config.enabled) body.enabled = enabled
  const dirty = Object.keys(body).length > 0
  return (
    <Modal title={`Edit ${config.name ?? config.provider}`} onClose={onClose}>
      <form
        aria-label="Edit auth config form"
        onSubmit={(e) => {
          e.preventDefault()
          if (dirty && !save.isPending) save.mutate(body)
        }}
      >
        <div className="field">
          <label htmlFor="ace-name">Name</label>
          <input id="ace-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={64} />
        </div>
        <div className="field">
          <label htmlFor="ace-secret">Rotate client secret</label>
          <input id="ace-secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} maxLength={2048} autoComplete="new-password" placeholder="Leave empty to keep the current secret" />
        </div>
        <div className="field">
          <label htmlFor="ace-scopes">Scopes</label>
          <input id="ace-scopes" value={scopes} onChange={(e) => setScopes(e.target.value)} />
        </div>
        <label className="check">
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} /> Workspace default
        </label>
        <label className="check">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Enabled
        </label>
        {save.error ? <p className="form-error" role="alert">{errorMessage(save.error)}</p> : null}
        <div className="modal-foot modal-foot--inline">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={!dirty || save.isPending} aria-busy={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function DeleteModal({ config, onClose }: { config: WorkspaceAuthConfig; onClose: () => void }) {
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const remove = useMutation({
    mutationFn: () => apiFetch(`/auth-configs/${config.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.authConfigs(workspaceId) })
      onClose()
    },
  })
  const inUse = isApiError(remove.error) && remove.error.status === 409
  return (
    <ConfirmAction
      title="Delete auth config"
      confirmLabel="Delete"
      pendingLabel="Deleting…"
      pending={remove.isPending}
      error={
        remove.error
          ? inUse
            ? 'Connected accounts still use this config, so it can’t be deleted. Edit it and turn off “Enabled” instead.'
            : errorMessage(remove.error)
          : null
      }
      onConfirm={() => remove.mutate()}
      onClose={onClose}
    >
      <p>
        Delete <strong>{config.name ?? config.provider}</strong> ({config.provider})? New connections fall back to the
        workspace default or the platform app.
      </p>
    </ConfirmAction>
  )
}

function Actions({ config, gate, onEdit, onDelete }: { config: WorkspaceAuthConfig; gate: Gate; onEdit: () => void; onDelete: () => void }) {
  const label = config.name ?? config.provider
  return (
    <>
      <button type="button" className="btn btn--sm" disabled={!gate.allowed} title={gate.reason || undefined} onClick={onEdit} aria-label={`Edit ${label}`}>
        Edit
      </button>
      <button type="button" className="btn btn--sm btn--ghost-danger" disabled={!gate.allowed} title={gate.reason || undefined} onClick={onDelete} aria-label={`Delete ${label}`}>
        Delete
      </button>
    </>
  )
}

export function AuthConfigsView() {
  const { workspaceId } = useSession()
  const canManage = useCan('manage_auth_configs')
  const byo = useFeature('auth_configs.byo')
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<WorkspaceAuthConfig | null>(null)
  const [deleting, setDeleting] = useState<WorkspaceAuthConfig | null>(null)
  const q = useQuery({
    queryKey: qk.authConfigs(workspaceId),
    queryFn: () => apiFetch<AuthConfigsResponse>('/auth-configs'),
    enabled: !!workspaceId,
  })
  const rows = q.data?.auth_configs ?? []
  const own = rows.filter((r) => r.scope === 'workspace')
  const platform = rows.filter((r) => r.scope === 'platform')
  const providers = [...new Set(platform.filter((r) => r.enabled).map((r) => r.provider))].sort()
  const createDisabled = !byo.allowed || providers.length === 0

  return (
    <div className="page">
      <PageHeader
        title="Auth Configs"
        count={q.data ? own.length : undefined}
        description="Bring your own OAuth app per provider so consent screens show your brand and your scopes. Platform apps are used when you don’t."
        actions={
          canManage && (
            <button type="button" className="btn btn--primary" disabled={createDisabled} onClick={() => setCreating(true)}>
              <Icon name="plus" size={14} /> New auth config
            </button>
          )
        }
      />
      {canManage && <PlanNote gate={byo}>Bring-your-own OAuth apps:</PlanNote>}

      <QueryState isLoading={q.isLoading} error={q.error} what="auth configs" onRetry={() => q.refetch()}>
        <section aria-labelledby="ac-own-h">
          <h2 id="ac-own-h">Your OAuth apps</h2>
          {own.length === 0 ? (
            <EmptyState
              icon="shield"
              title="No OAuth apps yet"
              body="Connections use Symone’s platform apps. Add your own app to control the consent screen and scopes."
            />
          ) : (
            <div className="table-wrap">
              <table className="table" aria-label="Your OAuth apps">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Provider</th>
                    <th>Client ID</th>
                    <th>Status</th>
                    <th>Redirect URI</th>
                    {canManage && <th><span className="sr-only">Actions</span></th>}
                  </tr>
                </thead>
                <tbody>
                  {own.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <div className="cell-strong">{c.name}</div>
                        <div className="cell-sub">{c.scopes.length} scopes</div>
                      </td>
                      <td>{c.display_name ?? c.provider}</td>
                      <td><code>{c.client_id}</code></td>
                      <td>
                        {c.enabled ? <Badge tone="success">Enabled</Badge> : <Badge>Disabled</Badge>}{' '}
                        {c.is_default && <Badge tone="info">Default</Badge>}
                      </td>
                      <td>
                        <code className="cell-sub">{c.redirect_uri}</code> <CopyButton value={c.redirect_uri} />
                      </td>
                      {canManage && (
                        <td className="cell-actions">
                          <Actions config={c} gate={byo} onEdit={() => setEditing(c)} onDelete={() => setDeleting(c)} />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section aria-labelledby="ac-platform-h">
          <h2 id="ac-platform-h">Platform apps</h2>
          <p className="cell-sub">Managed by Symone. Read-only.</p>
          <div className="table-wrap">
            <table className="table table--dense" aria-label="Platform apps">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Scopes</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {platform.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div className="cell-strong">{c.display_name ?? c.provider}</div>
                      <div className="cell-sub">{c.provider}</div>
                    </td>
                    <td className="cell-sub">{c.scopes.join(' ') || '—'}</td>
                    <td>{c.enabled ? <Badge tone="success">Enabled</Badge> : <Badge>Disabled</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </QueryState>

      {creating && <CreateModal providers={providers} onClose={() => setCreating(false)} />}
      {editing && <EditModal config={editing} onClose={() => setEditing(null)} />}
      {deleting && <DeleteModal config={deleting} onClose={() => setDeleting(null)} />}
    </div>
  )
}
